import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Settings as SettingsIcon,
  ArrowBack as ArrowBackIcon,
  DeleteForever as DeleteIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePasskey } from '../contexts/PasskeyContext';
import { useAuth } from '../contexts/AuthContext';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { EncryptionService } from '../services/encryptionService';
import { reEncryptThoughts } from '../services/thoughtService';
import { reEncryptDiaryEntries } from '../services/diaryService';
import { reEncryptJournalEntries } from '../services/journalService';
import { deleteAccount } from '../services/userService';
import Header from '../components/Header';

const TEST_VALUE = 'mythoughts.buzz';

export const PasskeySettings: React.FC = () => {
  const { passkey: currentPasskey, setPasskey, clearPasskey } = usePasskey();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // Change passkey state
  const [newPasskey, setNewPasskeyInput] = useState('');
  const [confirmPasskey, setConfirmPasskey] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [progressPercent, setProgressPercent] = useState(0);

  // Delete account state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePasskey, setDeletePasskey] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState('');

  const handleChangePasskey = async () => {
    setError('');
    setSuccess('');
    setProgress('');
    setProgressPercent(0);

    if (!newPasskey.trim()) {
      setError('Please enter a new passkey');
      return;
    }

    if (newPasskey.length < 4) {
      setError('Passkey must be at least 4 characters');
      return;
    }

    if (newPasskey !== confirmPasskey) {
      setError('Passkeys do not match');
      return;
    }

    if (!currentUser || !currentPasskey) {
      setError('User not authenticated or passkey not set');
      return;
    }

    if (newPasskey === currentPasskey) {
      setError('New passkey must be different from current passkey');
      return;
    }

    try {
      setLoading(true);
      
      let totalSuccess = 0;
      let totalFailed = 0;

      // 1. Re-encrypt thoughts
      setProgress('Re-encrypting thoughts...');
      setProgressPercent(10);
      const thoughtsResult = await reEncryptThoughts(
        currentUser.uid,
        currentPasskey,
        newPasskey,
        (current, total) => {
          const percent = 10 + Math.round((current / total) * 25);
          setProgressPercent(percent);
          setProgress(`Re-encrypting thought ${current} of ${total}...`);
        }
      );
      totalSuccess += thoughtsResult.success;
      totalFailed += thoughtsResult.failed;

      // 2. Re-encrypt diary entries
      setProgress('Re-encrypting diary entries...');
      setProgressPercent(40);
      const diaryResult = await reEncryptDiaryEntries(
        currentUser.uid,
        currentPasskey,
        newPasskey,
        (current, total) => {
          const percent = 40 + Math.round((current / total) * 25);
          setProgressPercent(percent);
          setProgress(`Re-encrypting diary entry ${current} of ${total}...`);
        }
      );
      totalSuccess += diaryResult.success;
      totalFailed += diaryResult.failed;

      // 3. Re-encrypt journal entries
      setProgress('Re-encrypting journal entries...');
      setProgressPercent(70);
      const journalResult = await reEncryptJournalEntries(
        currentUser.uid,
        currentPasskey,
        newPasskey,
        (current, total) => {
          const percent = 70 + Math.round((current / total) * 25);
          setProgressPercent(percent);
          setProgress(`Re-encrypting journal entry ${current} of ${total}...`);
        }
      );
      totalSuccess += journalResult.success;
      totalFailed += journalResult.failed;

      // 4. Update passkey verification
      setProgress('Updating passkey verification...');
      setProgressPercent(95);
      const encryptedTest = EncryptionService.encrypt(TEST_VALUE, newPasskey);
      const userDocRef = doc(db, 'users', currentUser.uid);
      await setDoc(
        userDocRef,
        {
          encryptedTest,
          updatedAt: new Date(),
        },
        { merge: true }
      );

      // 5. Update local passkey
      setPasskey(newPasskey);
      setProgressPercent(100);

      setProgress('');
      let successMsg = `✅ Passkey changed successfully! Re-encrypted ${totalSuccess} item(s).`;
      if (totalFailed > 0) {
        successMsg += ` ${totalFailed} item(s) could not be re-encrypted (they were locked).`;
      }
      successMsg += ' Redirecting to home...';
      setSuccess(successMsg);
      setNewPasskeyInput('');
      setConfirmPasskey('');

      // Redirect to home after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err: any) {
      console.error('Error changing passkey:', err);
      setError(`Failed to change passkey: ${err.message || 'Unknown error'}. Your data is safe.`);
      setProgress('');
      setProgressPercent(0);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasskey = () => {
    if (confirm('Warning: Resetting your passkey will log you out. Your existing data will remain encrypted and inaccessible without the old passkey. Continue?')) {
      clearPasskey();
      navigate('/');
    }
  };

  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
    setDeletePasskey('');
    setDeleteConfirmText('');
    setDeleteError('');
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeletePasskey('');
    setDeleteConfirmText('');
    setDeleteError('');
    setDeleteProgress('');
  };

  const handleDeleteAccount = async () => {
    setDeleteError('');

    // Validate passkey
    if (!deletePasskey.trim()) {
      setDeleteError('Please enter your passkey');
      return;
    }

    // Verify passkey is correct
    if (!currentUser) {
      setDeleteError('User not authenticated');
      return;
    }

    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists() && userDoc.data()?.encryptedTest) {
        try {
          const decrypted = EncryptionService.decrypt(
            userDoc.data().encryptedTest,
            deletePasskey
          );

          if (decrypted !== TEST_VALUE) {
            setDeleteError('Invalid passkey. Please try again.');
            return;
          }
        } catch (err) {
          setDeleteError('Invalid passkey. Please try again.');
          return;
        }
      } else {
        setDeleteError('Unable to verify passkey. Please try again.');
        return;
      }
    } catch (err) {
      setDeleteError('Error verifying passkey. Please try again.');
      return;
    }

    // Validate confirmation text
    if (deleteConfirmText !== 'DELETE MY ACCOUNT') {
      setDeleteError('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }

    try {
      setDeleteLoading(true);
      setDeleteProgress('Starting account deletion...');

      await deleteAccount((stage, current, total) => {
        if (total > 0) {
          setDeleteProgress(`${stage} (${current}/${total})`);
        } else {
          setDeleteProgress(stage);
        }
      });

      // Clear local passkey
      clearPasskey();

      // Close dialog and redirect to login
      setDeleteDialogOpen(false);
      navigate('/login');
    } catch (err: any) {
      console.error('Error deleting account:', err);
      setDeleteError(`Failed to delete account: ${err.message || 'Unknown error'}`);
    } finally {
      setDeleteLoading(false);
      setDeleteProgress('');
    }
  };

  return (
    <>
      <Header />
      <Container maxWidth="sm" sx={{ mt: 10, mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{ mb: 2 }}
          >
            Back to Home
          </Button>
        </Box>

        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <SettingsIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              Passkey Settings
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Your passkey encrypts all your thoughts, diary entries, and journals. When you change it, all your existing data will be re-encrypted with the new passkey.
            </Typography>
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {loading && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {progress}
              </Typography>
              <LinearProgress variant="determinate" value={progressPercent} />
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Change Passkey
          </Typography>

          <TextField
            fullWidth
            type={showNew ? 'text' : 'password'}
            label="New Passkey"
            value={newPasskey}
            onChange={(e) => setNewPasskeyInput(e.target.value)}
            disabled={loading}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNew(!showNew)} edge="end">
                    {showNew ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            type={showConfirm ? 'text' : 'password'}
            label="Confirm New Passkey"
            value={confirmPasskey}
            onChange={(e) => setConfirmPasskey(e.target.value)}
            disabled={loading}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !loading) {
                handleChangePasskey();
              }
            }}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end">
                    {showConfirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleChangePasskey}
            disabled={loading}
            sx={{ py: 1.5, mb: 2 }}
          >
            {loading ? 'Changing...' : 'Change Passkey'}
          </Button>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom color="error">
            Danger Zone
          </Typography>

          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Resetting your passkey will clear your local passkey and log you out. You'll need to
              re-enter your passkey next time to access your data.
            </Typography>
          </Alert>

          <Button
            fullWidth
            variant="outlined"
            color="warning"
            size="large"
            onClick={handleResetPasskey}
            disabled={loading}
            sx={{ py: 1.5, mb: 3 }}
          >
            Reset & Logout
          </Button>

          <Divider sx={{ my: 3 }} />

          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              ⚠️ Delete Account Permanently
            </Typography>
            <Typography variant="body2">
              This will permanently delete your account and all your data (thoughts, diary entries, journals). 
              This action cannot be undone.
            </Typography>
          </Alert>

          <Button
            fullWidth
            variant="contained"
            color="error"
            size="large"
            onClick={handleOpenDeleteDialog}
            disabled={loading}
            startIcon={<DeleteIcon />}
            sx={{ py: 1.5 }}
          >
            Delete My Account
          </Button>
        </Paper>
      </Container>

      {/* Delete Account Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningIcon />
          Delete Account Permanently
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              This action is irreversible!
            </Typography>
            <Typography variant="body2">
              All your data will be permanently deleted:
              <br />• All thoughts
              <br />• All diary entries
              <br />• All journal entries
              <br />• Your account and settings
            </Typography>
          </Alert>

          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}

          {deleteProgress && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {deleteProgress}
            </Alert>
          )}

          <TextField
            fullWidth
            type="password"
            label="Enter your passkey to confirm"
            value={deletePasskey}
            onChange={(e) => setDeletePasskey(e.target.value)}
            disabled={deleteLoading}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label='Type "DELETE MY ACCOUNT" to confirm'
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            disabled={deleteLoading}
            error={deleteConfirmText.length > 0 && deleteConfirmText !== 'DELETE MY ACCOUNT'}
            helperText={
              deleteConfirmText.length > 0 && deleteConfirmText !== 'DELETE MY ACCOUNT'
                ? 'Please type exactly: DELETE MY ACCOUNT'
                : ''
            }
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={deleteLoading || deleteConfirmText !== 'DELETE MY ACCOUNT' || !deletePasskey}
            startIcon={<DeleteIcon />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete Forever'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PasskeySettings;
